// React imports
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// MUI imports
import {
  Card,
  Grid,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton as MuiIconButton,
  DialogContentText,
  Tooltip,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import { Snackbar, Alert } from '@mui/material';

// MUI Icons imports
import {
  AccessTime, // for timestamps
  DevicesOther, // for device type
  Router, // for IP
} from '@mui/icons-material';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { Email } from '@mui/icons-material';

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import EmailPreferences from 'components/ProfileComponents/EmailPreferences';
import MDUserAvatar from "components/MDUserAvatar";
import AttachmentUploadModal from "../../components/CustomModals/AttachmentUploadModal";

// Other imports
import { useAuth } from "../../auth/Auth";
import { styled } from '@mui/material/styles';
import {deletePrivateObject, previewPrivateObject, uploadPrivateObject} from 'utils/S3';
import moment from 'moment';
import {frontendLogger} from 'utils/logger';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import colors from "assets/theme/base/colors";
import { useCountryCodes } from 'hooks/useCountryCodes';
import { formatTimezoneLabel, getTimezoneOptions } from 'utils/userTimezone';
import {
  DEFAULT_PHONE_DIAL_CODE,
  parseStoredPhone,
  composePhone,
  isExactTenDigitPhone,
} from 'utils/companyPhone';



const UploadInput = styled('input')({
  display: 'none',
});


const EditProfileDialog = ({
  open,
  onClose,
  onSave,
  profileData,
  editData,
  setEditData,
  imagePreview,
  onImageUpload,
  handleUploadSuccess,
  updateIsAttachmentModalOpen,
  isAttachmentModalOpen,
  axiosInstance,
  saving,
  phoneDialCode,
  setPhoneDialCode,
  phoneNumber,
  setPhoneNumber,
  timezoneValue,
  setTimezoneValue,
  timezoneOptions,
}) => {
  const { countryCodes, getByDialCode } = useCountryCodes();
  const hasPhoto = Boolean(imagePreview || profileData?.avatar);
  const selectedDial = getByDialCode(phoneDialCode) || { dialCode: phoneDialCode, country: phoneDialCode };

  return (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    keepMounted
    disableEscapeKeyDown
    PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
  >
    <Box className="pearl-popup-header"
      sx={{
        backgroundColor: colors.popupHeader.background,
        px: 3,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <MDTypography variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
        Edit Profile
      </MDTypography>
      <MuiIconButton
        aria-label="Close"
        onClick={onClose}
        disabled={saving}
        sx={{ color: '#FFFFFF' }}
      >
        <CloseIcon />
      </MuiIconButton>
    </Box>
    <DialogContent>
      <MDBox mt={2} mb={3} textAlign="center" position="relative">
        <MDBox>
          <AttachmentUploadModal
              isOpen={isAttachmentModalOpen}
              onClose={() => updateIsAttachmentModalOpen(false)}
              onUpload={handleUploadSuccess}
              axiosInstance={axiosInstance}
              allowedFileTypes="*/*"
              maxSizeInMB={5}
          />
        </MDBox>
        {hasPhoto ? (
          <MDAvatar
            src={imagePreview || profileData?.avatar}
            alt="Profile Preview"
            size="xxl"
            variant="rounded"
            sx={{
              width: 150,
              height: 150,
              margin: '0 auto',
              border: '4px solid white',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.14)',
            }}
          />
        ) : (
          <MDUserAvatar
            firstName={editData?.first_name || profileData?.first_name}
            middleName={editData?.middle_name || profileData?.middle_name}
            lastName={editData?.last_name || profileData?.last_name}
            size={150}
            variant="rounded"
          />
        )}
        <MDBox mt={2} display="flex" justifyContent="center" gap={1}>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<PhotoCamera />}
            onClick={() => updateIsAttachmentModalOpen(true)}
          >
            {hasPhoto ? 'Replace Photo' : 'Upload Photo'}
          </MDButton>
        </MDBox>
        <label htmlFor="icon-button-file">
          <UploadInput
            accept="image/*"
            id="icon-button-file"
            type="file"
            onChange={onImageUpload}
          />
        </label>
      </MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="First Name"
            value={editData.first_name}
            onChange={(e) => setEditData(prev => ({ ...prev, first_name: e.target.value }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Middle Name"
            value={editData.middle_name}
            onChange={(e) => setEditData(prev => ({ ...prev, middle_name: e.target.value }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Last Name"
            value={editData.last_name}
            onChange={(e) => setEditData(prev => ({ ...prev, last_name: e.target.value }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required // Add this
            error={!editData.last_name.trim()} // Show error if empty or only spaces
            helperText={!editData.last_name.trim() ? "Last Name is required" : ""} // Error message
          />
        </Grid>
        <Grid item xs={12}>
          <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: 'block', mb: 0.5, mt: 1 }}>
            Phone Number
          </MDTypography>
          <MDBox display="flex" gap={1} alignItems="flex-start">
            <Autocomplete
              options={countryCodes}
              value={selectedDial}
              onChange={(_, value) => {
                setPhoneDialCode(value?.dialCode || DEFAULT_PHONE_DIAL_CODE);
              }}
              getOptionLabel={(option) => option?.dialCode || ''}
              isOptionEqualToValue={(option, value) => option?.dialCode === value?.dialCode}
              renderOption={(props, option) => (
                <MDBox component="li" {...props} key={`${option.iso2}-${option.dialCode}`} display="flex" gap={1}>
                  <MDTypography variant="caption">{option.dialCode}</MDTypography>
                  <MDTypography variant="caption" color="text">{option.country}</MDTypography>
                </MDBox>
              )}
              slotProps={{ popper: { sx: { width: '280px !important' } } }}
              sx={{ flex: '0 0 110px', minWidth: 110 }}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="+91" />
              )}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="10-digit number"
              value={phoneNumber}
              helperText="Exactly 10 digits (country code separate)"
              inputProps={{ inputMode: 'numeric', maxLength: 10, pattern: '[0-9]*' }}
              onChange={(e) => {
                setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: 'block', mb: 0.5, mt: 1 }}>
            Timezone
          </MDTypography>
          <Autocomplete
              options={timezoneOptions}
              value={timezoneValue || null}
              onChange={(_, value) => setTimezoneValue(value)}
              getOptionLabel={(option) => formatTimezoneLabel(option)}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable
              renderInput={(params) => (
                  <TextField
                      {...params}
                      size="small"
                      placeholder="Search e.g. Dubai, Kolkata"
                      helperText="Used to display dates and times across the app"
                  />
              )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Department"
            value={editData.department}
            onChange={(e) => setEditData(prev => ({ ...prev, department: e.target.value }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Designation"
            value={editData.designation}
            onChange={(e) => setEditData(prev => ({ ...prev, designation: e.target.value }))}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <MDButton
        onClick={onClose}
        color="secondary"
        disabled={saving}
      >
        Cancel
      </MDButton>
      <MDButton
        onClick={onSave}
        color="info"
        disabled={saving}
        startIcon={saving && <CircularProgress size={20} color="inherit" />}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </MDButton>
    </DialogActions>
  </Dialog>
)};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return 'Not available';
  return moment(dateTime).format('MMM DD, YYYY, hh:mm A');
};

// Add this utility function near the top of the file, after the imports
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function UserProfile() {
  const { user, axiosInstance, refreshUserAvatar, updateUserAvatar, timezone, setTimezone } = useAuth();

  const [timezoneValue, setTimezoneValue] = useState(timezone);
  const timezoneOptions = useMemo(() => getTimezoneOptions([timezone]), [timezone]);

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  // const [editData, setEditData] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [saving, setSaving] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [schemaName, setSchemaName] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Replace the activeTab state with this
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === 'email' ? 1 : 0; // Default to 0 if no tab or invalid tab
  });

  const [deviceInfo, setDeviceInfo] = useState({
    browser: '',
    os: '',
    device: '',
  });
  const [locationInfo, setLocationInfo] = useState({
    city: '',
    country: '',
    ip: '',
  });

  // Add this to your state declarations
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const loadingRef = useRef(null);
  const ITEMS_PER_PAGE = 10;
  const [editData, setEditData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    department: '',
    designation: ''
  });
  const [phoneDialCode, setPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const { countryCodes } = useCountryCodes();

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';
    let device = 'Desktop';

    // Browser detection
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    else if (ua.includes('MSIE') || ua.includes('Trident/')) browser = 'Internet Explorer';

    // OS detection
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'MacOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    // Device detection
    if (/Mobi|Android/i.test(ua)) device = 'Mobile';
    else if (/Tablet|iPad/i.test(ua)) device = 'Tablet';

    return { browser, os, device };
  };

  const fetchLocationInfo = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      setLocationInfo({
        city: data.city,
        country: data.country_name,
        ip: data.ip,
      });
    } catch (error) {
      console.error('Error fetching location:', error);
    }
  };

  // Function to fetch login history
  const fetchLoginHistory = async (page = 0) => {
    if (loadingHistory || loadingMore) return;
    
    if (page === 0) {
      setLoadingHistory(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await axiosInstance.get(
        `/login-history/user/${user.id}?skip=${page * ITEMS_PER_PAGE}&limit=${ITEMS_PER_PAGE}`
      );
      
      if (page === 0) {
        setLoginHistory(response.data);
      } else {
        setLoginHistory(prev => [...prev, ...response.data]);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
      setSnackbar({
        open: true,
        message: 'Error loading login history',
        severity: 'error'
      });
    } finally {
      setLoadingHistory(false);
      setLoadingMore(false);
    }
  };

  // Load more history when the loading ref is in view
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingHistory && !loadingMore) {
        setLoadingMore(true);
        setHistoryPage(prev => {
          const nextPage = prev + 1;
          fetchLoginHistory(nextPage); // Fetch next page
          return nextPage;
        });
      }
    }, {
      rootMargin: '100px',
      threshold: 0.1
    });

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [loadingRef, loadingHistory, loadingMore]);

  // Initial fetch of login history
  useEffect(() => {
    fetchLoginHistory(0);
  }, []);


  // ------------------------------------------------------------------
  // PROFILE PICTURE ATTACHMENT
  // ------------------------------------------------------------------


  const [attachments, setAttachments] = useState([]);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  // Fetch existing attachment when the page loads up.

  useEffect(() => {
    fetchAttachments();
  }, []);

  // GET attachments

  const fetchAttachments = async () => {
    try {

      const response = await axiosInstance.get('/attachments/', {
        params: { parent_table: 'users', parent_id: user.id },
      });
      setAttachments(response.data || []);

    } catch (error) {

      console.error('Error fetching attachments:', error);
      const errorMessage = error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          'Error fetching attachments';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 5000
      });

    } finally {
      // If you have a loading state
      // setLoading(false);
    }
  };

  // When attachment exists, retrieve preview URL.

  useEffect(() => {
    const attachment = getImageAttachment();
    if (!attachment) {
      setImagePreviewUrl('');
      return;
    }

    let cancelled = false;

    const resolvePreview = async () => {
      try {

        if (!attachment.bucket_name || !attachment.storage_path) {
          throw new Error('Attachment storage metadata is invalid for strict R2 download');
        }

        const url = await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
        if (!cancelled) setImagePreviewUrl(url || '');

      } catch (error) {
        console.error('Error previewing attachment:', error);
      }
    };

    resolvePreview();
    return () => { cancelled = true; };
  }, [attachments]);

  // GET first attachment.

  const getImageAttachment = () => {
    const imageAttachments = (attachments || []).filter(
        (a) => a.file_type && a.file_type.startsWith('image/')
    );
    if (!imageAttachments.length) return null;
    return [...imageAttachments].sort((a, b) => b.id - a.id)[0];
  };

  // DELETE attachment

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axiosInstance.delete(`/attachments/${attachmentId}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
      // setSnackbar({ open: true, message: 'Attachment deleted successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setSnackbar({ open: true, message: 'Error deleting attachment', severity: 'error' });
    }
  };

  // Handle Upload PFP

  const handleUploadSuccess = async (uploadData) => {
    setIsAttachmentModalOpen(false);
    const linkedAttachment = getImageAttachment();

    // Replace: remove the old pfp image attachment first, if any
    if (linkedAttachment) {
      await handleDeleteAttachment(linkedAttachment.id); // updates `attachments` state locally + logs on failure
    }

    const uploadDataPayload = {
      file_name: uploadData.file.name,
      file_type: uploadData.file.type,
      file_size: uploadData.file.size,
      bucket_name: uploadData.bucket,
      storage_path: uploadData.path,
      description: uploadData.description || '',
      parent_table: 'users',
      parent_id: user.id,
      is_active: true,
      is_private: uploadData.isPrivate || false,
      file_url: uploadData.url,
      signed_url_expiry: uploadData.signedUrlExpiry || null,
    };

    try {
      await axiosInstance.post('/attachments/', uploadDataPayload);
      setSnackbar({ open: true, message: 'File uploaded successfully!', severity: 'success' });
      // Keep top-nav avatar in sync with the new profile attachment
      if (typeof refreshUserAvatar === 'function') {
        await refreshUserAvatar();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({ open: true, message: 'Error uploading file', severity: 'error' });
    } finally {
      fetchAttachments();
    }
  };

  // Remove the schemaResponse from the Promise.all
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Extract schema name from URL
        const hostname = window.location.hostname;
        const schema = hostname.split('.')[0];
        const schemaName = `tenant_${schema}`;

        // Set schema name
        setSchemaName(schemaName);

        // Fetch profile data
        const profileResponse = await axiosInstance.get(`/users/${user.id}`);
        setProfileData(profileResponse.data);

      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading data',
          severity: 'error'
        });
      } finally {
        setLoading(false);
        setShouldRefresh(false);
      }
    };

    if (shouldRefresh || !profileData) {
      fetchData();
    }
  }, [user.id, shouldRefresh]);

  useEffect(() => {
    setDeviceInfo(getDeviceInfo());
    fetchLocationInfo();
    fetchLoginHistory();
  }, []);

  // Add this function to handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Update URL when tab changes
    const tabValue = newValue === 1 ? 'email' : 'profile';
    navigate(`${location.pathname}?tab=${tabValue}`, { replace: true });
  };

  // Update the useEffect that runs on component mount to handle direct URL access
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'email') {
      setActiveTab(1);
    } else if (tab === 'profile') {
      setActiveTab(0);
    }
  }, [searchParams]);

  const uploadAvatarToR2 = async (file, userId) => {
    try {
      if (!schemaName) {
        throw new Error('Schema name not available');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const uploadResult = await uploadPrivateObject({
        axiosInstance,
        file,
        env: process.env.REACT_APP_STORAGE_ENV,
        featurePath: 'avatars',
        scope: 'user',
      });

      return {
        path: uploadResult.path,
        url: uploadResult.url
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };



  const validateImage = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPG, PNG, or GIF image.');
    }

    if (file.size > maxSize) {
      throw new Error('File is too large. Maximum size is 5MB.');
    }

    return true;
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateImage(file);
        setUploadedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error'
        });
      }
    }
  };

  const handleResetPassword = async () => {
    setResetPasswordDialog(true);
  };

  const handleConfirmReset = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.post('/sendresetemail/', {
        reset_link: `${window.location.origin}/reset-password`
      });

      setResetPasswordDialog(false);
      setSnackbar({
        open: true,
        message: response.data.message || 'Password reset link has been sent to your email',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error sending reset password email:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to send reset password email',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelReset = () => {
    setResetPasswordDialog(false);
  };

  const handleEditOpen = useCallback(() => {
    const parsed = parseStoredPhone(profileData?.phone, countryCodes);
    setPhoneDialCode(parsed.dialCode);
    setPhoneNumber(parsed.number);
    setEditData({
      first_name: profileData?.first_name || '',
      middle_name: profileData?.middle_name || '',
      last_name: profileData?.last_name || '',
      phone: profileData?.phone || '',
      department: profileData?.department || '',
      designation: profileData?.designation || '',
    });
    setTimezoneValue(timezone);
    setOpenEdit(true);
  }, [profileData, countryCodes, timezone]);

  const handleEditClose = () => {
    setOpenEdit(false);
    setImagePreview(null);
    setUploadedImage(null);
    setPhoneDialCode(DEFAULT_PHONE_DIAL_CODE);
    setPhoneNumber('');
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
        if (!schemaName) {
            throw new Error('Schema name not available. Please try again.');
        }

        // Validation for last name
        if (!editData.last_name || !editData.last_name.trim()) {
            setSnackbar({
                open: true,
                message: 'Last Name is required',
                severity: 'error'
            });
            setSaving(false);
            return;
        }

        // Phone number validation - country code + exactly 10 digits
        const digits = String(phoneNumber || '').replace(/\D/g, '');
        if (digits && !isExactTenDigitPhone(digits)) {
            setSnackbar({
                open: true,
                message: 'Phone number must be exactly 10 digits (excluding country code)',
                severity: 'error'
            });
            setSaving(false);
            return;
        }

        const composedPhone = digits ? composePhone(phoneDialCode, digits) : '';
        
        const formData = new FormData();

        // Append fields, ensuring empty strings are sent as empty strings
        formData.append('first_name', (editData.first_name || '').trim());
        formData.append('middle_name', editData.middle_name === '' ? '' : (editData.middle_name || ''));
        formData.append('last_name', editData.last_name.trim());
        formData.append('phone', composedPhone);
        formData.append('department', editData.department || '');
        formData.append('designation', editData.designation || '');

        // If there's a new image to upload
        let avatarUrl = null;
        if (uploadedImage) {
            try {
                // if (profileData?.avatar) {
                //     await deleteOldAvatar(profileData.avatar);
                // }
                const avatarUploadResult = await uploadAvatarToR2(uploadedImage, user.id);
                avatarUrl = avatarUploadResult.url;
                if (!avatarUrl) {
                    throw new Error('Failed to get upload URL');
                }
            } catch (uploadError) {
                console.error('Upload error:', uploadError);
                throw new Error('Failed to upload image');
            }
        }

        if (avatarUrl) {
            formData.append('avatar', avatarUrl);
        }

        const response = await axiosInstance.patch(
            `/users/${user.id}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        setProfileData(response.data);

        let timezoneError = null;
        if (timezoneValue && timezoneValue !== timezone) {
          try {
            await setTimezone(timezoneValue);
          } catch (tzError) {
            const detail = tzError.response?.data?.detail;
            timezoneError = typeof detail === 'string' ? detail : 'Failed to update timezone';
          }
        }

        if (response.data?.avatar && typeof updateUserAvatar === 'function') {
          updateUserAvatar(response.data.avatar);
        } else if (typeof refreshUserAvatar === 'function') {
          await refreshUserAvatar();
        }
        handleEditClose();
        setSnackbar({
            open: true,
            message: timezoneError
              ? `Profile updated, but timezone was not saved: ${timezoneError}`
              : 'Profile updated successfully',
            severity: timezoneError ? 'warning' : 'success',
        });
        setShouldRefresh(true);

        // Log the user profile update
        await frontendLogger.logUpdate('User', {
            id: user.id,
            updated_fields: Object.keys(editData),
            changes: editData,
            action: 'edit',
            user: user.username,
            timestamp: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Error updating profile:', error);
        setSnackbar({
            open: true,
            message: error.message || 'Failed to update profile',
            severity: 'error'
        });
    } finally {
        setSaving(false);
    }
};
  const deleteOldAvatar = async (url) => {
    if (!url) return;

    try {
      let fullPath = url;
      if (url.includes('/avatar/')) {
        fullPath = url.split('/avatar/')[1];
      } else if (url.startsWith('http')) {
        const parsed = new URL(url);
        fullPath = parsed.pathname.replace(/^\/+/, '');
      }
      await deletePrivateObject({
        bucketName: 'unused',
        key: fullPath
      });
    } catch (error) {
      console.error('Error deleting old avatar:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="calc(100vh - 200px)"
        >
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  const renderSection = (title, fields) => (
    <MDBox
      mb={2}
      p={2}
      borderRadius="lg"
      sx={{
        backgroundColor: '#ffffff',
        boxShadow: 3
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6" fontWeight="medium" color="dark">{title}</MDTypography>
      </MDBox>
      <Grid container spacing={2}>
        {fields.map((item) => {
          let displayValue = item.value || profileData[item.field] || 'Not available';
          
          // Capitalize first letter for specific fields
          if (typeof displayValue === 'string' && displayValue && 
              (item.field === 'username' || 
               item.field === 'full_name' || 
               item.field === 'manager_name' || 
               item.field === 'created_by_name' || 
               item.field === 'updated_by_name')) {
            displayValue = capitalizeFirstLetter(displayValue);
          }

          return (
            <Grid item xs={12} md={6} key={item.field}>
              <MDBox
                display="flex"
                alignItems="flex-start"
                gap={1}
                sx={{ minWidth: 0, width: '100%' }}
              >
                {item.icon && (
                  <Box sx={{ color: 'info.main', flexShrink: 0, mt: 0.25 }}>
                    {item.icon}
                  </Box>
                )}
                <MDBox sx={{ minWidth: 0, flex: 1 }}>
                  <MDTypography variant="button" fontWeight="regular" color="text">
                    <strong>{item.label || item.field}:</strong>
                  </MDTypography>
                  {item.customRender ? (
                    item.customRender()
                  ) : (
                    <MDTypography
                      component="span"
                      variant="button"
                      fontWeight="regular"
                      sx={{
                        ml: 1,
                        display: 'inline',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {displayValue}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Grid>
          );
        })}
      </Grid>
    </MDBox>
  );

  const formatFullName = (firstName, middleName, lastName) => {
    const names = [
      capitalizeFirstLetter(firstName),
      capitalizeFirstLetter(middleName),
      capitalizeFirstLetter(lastName)
    ].filter(Boolean); // This removes empty strings

    return names.join(' ') || 'No Name Set';
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={2} py={2}>
        <Grid container spacing={2}>
          {/* Header Card */}
          <Grid item xs={12}>
            <Card
              sx={{
                position: 'relative',
                overflow: 'visible',
                mb: 3,
                background: 'linear-gradient(195deg, #1565C8, #5B9BD5)',
                boxShadow: '0 8px 20px -12px rgba(0, 158, 219, 0.6)'
              }}
            >
              <MDBox
                position="relative"
                p={3}
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                alignItems="center"
                gap={2}
              >
                {/* Avatar Section */}
                <MDUserAvatar
                  firstName={profileData?.first_name}
                  middleName={profileData?.middle_name}
                  lastName={profileData?.last_name}
                  avatarUrl={imagePreviewUrl}
                  size={150}
                  variant="rounded"
                />

                {/* Profile Info */}
                <MDBox flex={1} color="white">
                  <MDTypography variant="h4" fontWeight="medium" color="white">
                    {formatFullName(
                      profileData?.first_name,
                      profileData?.middle_name,
                      profileData?.last_name
                    )}
                  </MDTypography>
                  <MDTypography variant="body2" color="white" opacity={0.8}>
                    {profileData?.designation || 'No designation set'}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="white"
                    opacity={0.8}
                    sx={{
                      display: 'block',
                      maxWidth: '100%',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {profileData?.department}
                    {profileData?.department && profileData?.email ? ' • ' : ''}
                    {profileData?.email}
                  </MDTypography>
                </MDBox>

                {/* Action Buttons */}
                <MDBox
                  display="flex"
                  gap={1}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                >
                  <MDButton
                    variant="contained"
                    color="white"
                    onClick={handleResetPassword}
                    startIcon={<LockResetIcon />}
                  >
                    Reset Password
                  </MDButton>
                  <MDButton
                    variant="contained"
                    color="white"
                    startIcon={<EditIcon />}
                    onClick={handleEditOpen}
                  >
                    Edit Profile
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab 
                    label="Profile Information" 
                    onClick={() => navigate(`${location.pathname}?tab=profile`)}
                  />
                  <Tab 
                    label="Email Settings" 
                    icon={<Email />} 
                    iconPosition="start"
                    onClick={() => navigate(`${location.pathname}?tab=email`)}
                  />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      {renderSection('Profile Information', [
                        {
                          field: 'full_name',
                          label: 'Full Name',
                          value: formatFullName(
                            profileData?.first_name,
                            profileData?.middle_name,
                            profileData?.last_name
                          ),
                        },
                        { 
                          field: 'username', 
                          label: 'Username',
                          value: capitalizeFirstLetter(profileData?.username)
                        },
                        { field: 'email', label: 'Email' },
                        { field: 'phone', label: 'Phone' },
                        { field: 'timezone', label: 'Timezone', value: formatTimezoneLabel(timezone) },
                        { field: 'department', label: 'Department' },
                        {
                          field: 'manager_name',
                          label: 'Manager',
                          customRender: () => (
                            profileData?.manager_id ? (
                              <MDTypography
                                component="span"
                                variant="button"
                                color="info"
                                fontWeight="medium"
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                  ml: 1
                                }}
                                onClick={() => navigate(`/users/${profileData.manager_id}/profile`)}
                              >
                                {capitalizeFirstLetter(profileData.manager_name || 'Not assigned')}
                              </MDTypography>
                            ) : (
                              <MDTypography
                                component="span"
                                variant="button"
                                fontWeight="regular"
                                sx={{ ml: 1 }}
                              >
                                Not assigned
                              </MDTypography>
                            )
                          )
                        },
                        {
                          field: 'role',
                          label: 'Role',
                          value: profileData?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        },
                        { field: 'joining_date', label: 'Joining Date', value: formatDateTime(profileData?.joining_date) },
                        { field: 'last_login', label: 'Last Login', value: formatDateTime(profileData?.last_login) }
                      ])}
                    </Grid>

                    <Grid item xs={12}>
                      {renderSection('Audit Information', [
                        { 
                          field: 'created_by_name', 
                          label: 'Created By', 
                          value: capitalizeFirstLetter(profileData?.created_by_name),
                          readOnly: true 
                        },
                        { field: 'created_at', label: 'Created At', value: formatDateTime(profileData?.created_at), readOnly: true },
                        { 
                          field: 'updated_by_name', 
                          label: 'Updated By', 
                          value: capitalizeFirstLetter(profileData?.updated_by_name),
                          readOnly: true 
                        },
                        { field: 'updated_at', label: 'Updated At', value: formatDateTime(profileData?.updated_at), readOnly: true }
                      ])}
                    </Grid>
                  </Grid>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <EmailPreferences />
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>

          {/* Login History Card */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <MDBox p={2} lineHeight={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  Login History
                </MDTypography>
              </MDBox>
              <MDBox
                p={1.5}
                sx={{
                  height: '480px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'info.light',
                    borderRadius: '2px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: '2px',
                  }
                }}
              >
                {loginHistory.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {loginHistory.map((login, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          p: 0.75,
                          bgcolor: 'grey.100',
                          borderRadius: 1,
                          borderLeft: '2px solid',
                          borderLeftColor: 'info.main',
                          '&:hover': {
                            bgcolor: 'grey.200',
                          },
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <AccessTime sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                          <MDTypography variant="caption" fontWeight="medium" sx={{ flex: 1 }}>
                            {formatDateTime(login.login_timestamp)}
                          </MDTypography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                            <Tooltip title="Device Type" arrow placement="top">
                              <DevicesOther sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            </Tooltip>
                            <MDTypography
                              variant="caption"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: 'text.secondary'
                              }}
                            >
                              {login.device_type || 'Unknown'} • {login.browser || 'Unknown'}
                            </MDTypography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="IP Address" arrow placement="top">
                              <Router sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            </Tooltip>
                            <MDTypography variant="caption" color="text.secondary">
                              {login.ip_address || 'Unknown IP'}
                            </MDTypography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                    {loadingMore && (
                      <Box display="flex" justifyContent="center" p={1}>
                        <CircularProgress size={20} color="info" />
                      </Box>
                    )}
                    <div ref={loadingRef} style={{ height: '20px', marginTop: '10px' }} />
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={2}>
                    {loadingHistory ? (
                      <CircularProgress size={24} color="info" />
                    ) : (
                      <MDTypography variant="caption" color="text.secondary">
                        No login history available
                      </MDTypography>
                    )}
                  </Box>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      

      {/* Dialogs */}
      <EditProfileDialog
        open={openEdit}
        onClose={handleEditClose}
        onSave={handleSaveProfile}
        profileData={profileData}
        editData={editData}
        setEditData={setEditData}
        imagePreview={imagePreviewUrl}
        onImageUpload={handleImageUpload}
        handleUploadSuccess={handleUploadSuccess}
        updateIsAttachmentModalOpen={setIsAttachmentModalOpen}
        isAttachmentModalOpen={isAttachmentModalOpen}
        axiosInstance={axiosInstance}
        saving={saving}
        phoneDialCode={phoneDialCode}
        setPhoneDialCode={setPhoneDialCode}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        timezoneValue={timezoneValue}
        setTimezoneValue={setTimezoneValue}
        timezoneOptions={timezoneOptions}
      />

      <Dialog
        open={resetPasswordDialog}
        onClose={handleCancelReset}
        aria-labelledby="reset-password-dialog-title"
        aria-describedby="reset-password-dialog-description"
      >
        <DialogTitle id="reset-password-dialog-title">
          Confirm Password Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-password-dialog-description">
            Are you sure you want to reset your password? A reset link will be sent to your email address: {profileData?.email}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleCancelReset}
            color="secondary"
            disabled={saving}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmReset}
            color="info"
            disabled={saving}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? 'Sending...' : 'Reset Password'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default UserProfile;